import { z } from 'zod';
import type { Order, OrderItem } from '@/types/order-type';
import type { Customer } from '@/types/customer-type';
// import type { ChatMessage } from "@/types/chat";
import type { Product } from '@/types/product-type';
import { productSchema } from '@/types/product-type';

// Extend types with additional client-side properties
export interface CustomerWithStatus extends Customer {
  isOnline?: boolean;
  unreadCount?: number;
  lastMessage?: string;
  lastMessageTime?: Date;
}

export interface OrderWithItems extends Order {
  items: OrderItem[];
  customer?: Customer;
}

export interface ChatMessageWithProductLink {
  product?: Product;
}

export type NavItem = {
  title: string;
  href: string;
  icon: React.ReactNode;
  current: boolean;
};

export type MobileNavItem = {
  title: string;
  href: string;
  icon: React.ReactNode;
  current: boolean;
};

// Customer form schema - form-friendly version (no nulls)
export const customerSchema = z.object({
  name: z.string().min(2, 'Name is required and must be at least 2 characters'),
  phone: z.string().optional(),
  address: z.string().optional(),
  saleChannel: z.string().default('Facebook'),
  notTransfer: z.boolean().default(false),
});

export type CustomerFormValues = z.infer<typeof customerSchema>;

export type CustomerSearchParams = {
  page: string;
  limit: string;
  search: string;
};

// Re-export Product form types from central schema
export { productSchema };

export interface ProductAttributeCreateRequest {
  productId: string;
  name: string;
  displayName: string;
  type?: string;
  isActive?: boolean;
}

export interface ProductAttributeUpdateRequest {
  name?: string;
  displayName?: string;
  type?: string;
  isActive?: boolean;
}

export interface ProductAttributeValueCreateRequest {
  attributeId: string;
  value: string;
  displayValue?: string;
  colorCode?: string;
  sortOrder: number;
  isActive: boolean;
}

export interface ProductAttributeValueUpdateRequest {
  value?: string;
  displayValue?: string;
  colorCode?: string;
  sortOrder?: number;
  isActive?: boolean;
}

export type ProductAttributeSortOption =
  | 'id,asc'
  | 'id,desc'
  | 'name,asc'
  | 'name,desc'
  | 'createdAt,asc'
  | 'createdAt,desc';

// Base user schema fields (shared between create and update)
// Validation rules match backend UserCreateRequest DTO
const userBaseSchema = z.object({
  firstName: z
    .string()
    .min(1, 'First name is required')
    .max(50, 'First name cannot exceed 50 characters'),
  lastName: z
    .string()
    .min(1, 'Last name is required')
    .max(50, 'Last name cannot exceed 50 characters'),
  username: z
    .string()
    .min(3, 'Username must be between 3 and 50 characters')
    .max(50, 'Username must be between 3 and 50 characters')
    .regex(
      /^[a-zA-Z0-9._-]+$/,
      'Username can only contain letters, numbers, dots, underscores, and hyphens',
    ),
  phone: z.string().optional(),
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Invalid email format')
    .max(100, 'Email cannot exceed 100 characters'),
  state: z.number(),
  roles: z
    .array(z.string())
    .min(1, 'At least one role is required')
    .refine(
      (val) => val.every((role) => ['ROLE_USER', 'ROLE_MANAGER', 'ROLE_ADMIN'].includes(role)),
      'Roles must be one or more of: ROLE_USER, ROLE_MANAGER, ROLE_ADMIN',
    ),
});

// Password validation pattern: at least one lowercase, one uppercase, and one digit
const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).*$/;

// User create schema - password is required
export const userCreateSchema = userBaseSchema
  .extend({
    password: z
      .string()
      .min(8, 'Password must be between 8 and 100 characters')
      .max(100, 'Password must be between 8 and 100 characters')
      .regex(
        passwordPattern,
        'Password must contain at least one lowercase letter, one uppercase letter, and one digit',
      ),
    confirmPassword: z.string().optional(),
  })
  .refine(
    (data) => {
      // Password and confirmPassword must match
      if (data.password && data.confirmPassword) {
        return data.password === data.confirmPassword;
      }
      // If password is provided but confirmPassword is not, it's an error
      if (data.password && !data.confirmPassword) {
        return false;
      }
      return true;
    },
    {
      message: 'Passwords do not match',
      path: ['confirmPassword'],
    },
  );

// User update schema - password is optional
export const userUpdateSchema = userBaseSchema
  .extend({
    password: z
      .string()
      .min(8, 'Password must be between 8 and 100 characters')
      .max(100, 'Password must be between 8 and 100 characters')
      .regex(
        passwordPattern,
        'Password must contain at least one lowercase letter, one uppercase letter, and one digit',
      )
      .optional(),
    confirmPassword: z.string().optional(),
  })
  .refine(
    (data) => {
      // If a password is being set, confirmPassword must match it
      if (data.password && data.confirmPassword) {
        return data.password === data.confirmPassword;
      }
      // If password is provided but confirmPassword is not, it's an error
      if (data.password && !data.confirmPassword) {
        return false;
      }
      return true;
    },
    {
      message: 'Passwords do not match',
      path: ['confirmPassword'],
    },
  );

// Union type for form values (works for both create and update)
export type UserFormValues = z.infer<typeof userCreateSchema> | z.infer<typeof userUpdateSchema>;

// Legacy export for backward compatibility (use userCreateSchema for new code)
export const userSchema = userCreateSchema;
